import React from 'react';
import { Search, Calendar, CheckCircle, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { Client } from '../types';

interface UsageStatsBarProps {
    client: Client;
    usage: {
        searches: number;
        interviews: number;
        hires: number;
    };
    onUpgrade?: () => void;
}

const UsageStatsBar: React.FC<UsageStatsBarProps> = ({ client, usage, onUpgrade }) => {
    if (!client.plan_tier || client.plan_tier === 'None' || client.plan_tier === 'Basic') {
        return null; // Don't show for None/Basic
    }

    const getStatus = (used: number, limit: number) => {
        if (!limit || limit === 0) return { color: 'text-slate-400', label: '∞', isLow: false };
        const left = limit - used;
        const percentage = (left / limit) * 100;
        const isLow = percentage <= 20;
        return {
            color: isLow ? 'text-orange-600' : 'text-slate-600',
            label: `${used} / ${limit} (${left} left)`,
            isLow
        };
    };

    const searchesStatus = getStatus(usage.searches, client.searches_limit || 0);
    const interviewsStatus = getStatus(usage.interviews, client.interviews_limit || 0);
    const hiresStatus = getStatus(usage.hires, client.hires_limit || 0);

    const showUpgrade = searchesStatus.isLow || interviewsStatus.isLow || hiresStatus.isLow;
    const resetDate = client.period_end ? new Date(client.period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A';

    return (
        <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 px-6 py-2 flex items-center justify-between text-xs font-medium shadow-sm transition-all">
            <div className="flex items-center gap-6">
                <span className="text-slate-400 font-bold uppercase tracking-wider text-[10px]">Credits this period</span>

                <div className={`flex items-center gap-2 ${searchesStatus.color}`}>
                    <Search size={14} className={searchesStatus.isLow ? "animate-pulse" : ""} />
                    <span>Searches: <strong>{searchesStatus.label}</strong></span>
                </div>

                <div className={`flex items-center gap-2 ${interviewsStatus.color}`}>
                    <Calendar size={14} className={interviewsStatus.isLow ? "animate-pulse" : ""} />
                    <span>Interviews: <strong>{interviewsStatus.label}</strong></span>
                </div>

                <div className={`flex items-center gap-2 ${hiresStatus.color}`}>
                    <CheckCircle size={14} className={hiresStatus.isLow ? "animate-pulse" : ""} />
                    <span>Hires: <strong>{hiresStatus.label}</strong></span>
                </div>

                <div className="h-4 w-px bg-slate-200 mx-2" />
                <span className="text-slate-400">Reset: {resetDate}</span>
            </div>

            {showUpgrade && (
                <button
                    onClick={onUpgrade}
                    className="flex items-center gap-1.5 bg-orange-50 text-orange-700 border border-orange-200 px-3 py-1 rounded-full hover:bg-orange-100 transition-colors"
                >
                    <AlertTriangle size={12} />
                    <span className="font-bold">Low Credits</span>
                    <ArrowUpCircle size={12} className="ml-1" />
                    <span className="underline">Upgrade / Add-on</span>
                </button>
            )}
        </div>
    );
};

export default UsageStatsBar;
